// Browser-side checkout: turn the bag into a PlazaStack Checkout Session
// (Stripe-hosted) and send the shopper there.
//
// success_url / cancel_url live on THIS domain: Stripe returns the shopper to
// /checkout/success?session_id=... (we poll the session, then tidy the bag)
// or back to /cart with the bag intact.
//
// Safety rules (same as the reviewed Wix-era bridge):
//   - ONE random Idempotency-Key per checkout attempt, tied to a fingerprint of
//     the cart. A retry of the same cart (double-click, lost response,
//     `checkout_in_progress`) reuses it so PlazaStack hands back the same
//     session instead of reserving stock twice. A changed cart, or an attempt
//     older than the session lifetime, gets a fresh key. Keys are never derived
//     from cart contents, so two shoppers can never share a session.
//   - Quantities are never changed on the way out: more than 10 of an item, or
//     more than 20 lines, is refused with a message, not clamped.
//   - What was bought is saved per Stripe session (np:pending-checkout:<id>) so
//     the confirmation page can tidy exactly those lines and report GA4 from
//     it, never from whatever is in the bag later.
//   - All storage access sits in try/catch (merely touching window.localStorage
//     can throw) and the whole flow is wrapped so the button never sticks.
//
// Raw platform errors are never shown. CheckoutError carries a shopper-safe
// message; the cart UI adds the phone / Instagram fallback.

'use client';

import { createCheckout, getCheckoutSession, insufficientStockVariantIds } from './plazastack/client';
import { PsError } from './plazastack/types';
import type { AnalyticsItem } from './analytics';
import { MAX_LINES, MAX_QTY_PER_LINE, overCap, type BoughtLine } from './cart-math';
import type { CartLine } from './cart-store';
import { attemptKeyFor, dropCurrentAttempt, noteAttemptSession, readJson, removeKey, retireAttemptForSession, writeJson } from './checkout-attempt';

export class CheckoutError extends Error {
  /** Variant ids the server said cannot be fulfilled right now (409 insufficient_stock). */
  readonly unavailableVariantIds: string[];
  constructor(message: string, unavailableVariantIds: string[] = []) {
    super(message);
    this.name = 'CheckoutError';
    this.unavailableVariantIds = unavailableVariantIds;
  }
}

// One snapshot per Stripe session (two tabs never overwrite each other).
const pendingKey = (sessionId: string) => `np:pending-checkout:${sessionId}`;

/** What was submitted for one Stripe session, read back on /checkout/success. */
export interface PendingCheckout {
  sessionId: string;
  /** Bag lines this checkout bought, with the quantity bought. */
  lines: BoughtLine[];
  /** GA4 items and merchandise value (before shipping and tax). */
  items: AnalyticsItem[];
  value: number;
  createdAt: number;
}

export function readPendingCheckout(sessionId: string): PendingCheckout | null {
  const pending = readJson<PendingCheckout>('localStorage', pendingKey(sessionId));
  if (!pending || pending.sessionId !== sessionId || !Array.isArray(pending.lines) || !Array.isArray(pending.items)) return null;
  // The older Wix-cart bridge wrote a different shape under the same key; a
  // snapshot we can't read exactly is ignored (no cleanup, no GA4).
  const ok = pending.lines.every((l) => l && typeof l.variantId === 'string' && Number.isInteger(l.qty) && l.qty > 0);
  return ok && typeof pending.value === 'number' ? pending : null;
}

/** Retire one session's snapshot, and the attempt key only if it belongs to that session. */
export function forgetPendingCheckout(sessionId: string): void {
  removeKey('localStorage', pendingKey(sessionId));
  retireAttemptForSession(sessionId);
}

const GENERIC = 'We could not start checkout just now.';

/**
 * Creates the session and navigates to Stripe. Resolves only if the redirect
 * was issued (the page is about to unload); throws CheckoutError otherwise.
 */
export async function startCheckout(lines: CartLine[]): Promise<void> {
  try {
    await runCheckout(lines);
  } catch (err) {
    if (err instanceof CheckoutError) throw err;
    // Never leave the button stuck on "Redirecting…".
    console.error('[checkout] unexpected error', err);
    throw new CheckoutError(GENERIC);
  }
}

async function runCheckout(lines: CartLine[], retriedExpired = false): Promise<void> {
  if (lines.length === 0) throw new CheckoutError('Your bag is empty.');
  if (lines.some((l) => l.unavailable)) {
    throw new CheckoutError('Remove the items that are no longer available, then check out.');
  }
  // Refuse rather than silently change what the shopper pays for.
  const over = lines.find(overCap);
  if (over) {
    throw new CheckoutError(
      over.qty > MAX_QTY_PER_LINE
        ? `Online orders are limited to ${MAX_QTY_PER_LINE} of each item. Lower ${over.name} to ${MAX_QTY_PER_LINE} or fewer.`
        : `Only ${Math.max(1, Math.floor(over.maxQty))} of ${over.name} can be ordered right now. Lower the quantity to continue.`,
      [over.variantId],
    );
  }
  if (lines.length > MAX_LINES) {
    throw new CheckoutError(`Online orders are limited to ${MAX_LINES} different items. Split your order into two.`);
  }

  const items = lines.map((l) => ({ variant_id: l.variantId, quantity: l.qty }));
  const fingerprint = JSON.stringify([...items].sort((a, b) => a.variant_id.localeCompare(b.variant_id)));
  const idempotencyKey = attemptKeyFor(fingerprint);
  const origin = window.location.origin;

  let session;
  try {
    session = await createCheckout(
      {
        items,
        // {CHECKOUT_SESSION_ID} is substituted by Stripe; it must stay literal.
        success_url: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url:  `${origin}/cart`,
      },
      idempotencyKey,
    );
  } catch (err) {
    if (err instanceof PsError) {
      if (err.code === 'insufficient_stock') {
        throw new CheckoutError(
          'Part of your bag just sold out or has fewer left than you chose. Please review it and try again.',
          insufficientStockVariantIds(err),
        );
      }
      const detailCode = (err.details as { code?: string } | undefined)?.code;
      if (detailCode === 'checkout_in_progress') {
        // The same key is reused on the next tap, so this resolves to the same session.
        throw new CheckoutError('Your checkout is still being prepared. Wait a few seconds and tap Checkout again.');
      }
      if (err.code === 'rate_limited') {
        throw new CheckoutError('Checkout is busy. Please wait a moment and try again.');
      }
    }
    // Network failure (the response may have been lost: the stored key makes
    // the next tap safe), 5xx, tenant not ready, anything else: no platform text.
    console.error('[checkout] create failed', err);
    throw new CheckoutError(GENERIC);
  }

  const url = session.checkout_url;
  const urlOk = typeof url === 'string' && /^https:\/\//i.test(url);

  // PlazaStack replays a key's original response for 24 h, so this may be an
  // old session. Near or past its expiry, ask PlazaStack instead of trusting
  // the clock: only `expired` (set by the webhook that releases the stock)
  // allows a new key; rotating any earlier could hold the stock twice.
  const expiresAt = session.expires_at ? Date.parse(session.expires_at) : NaN;
  // A replay of an expired session may carry no URL at all (Stripe only
  // returns it while the session is open), so a missing URL also triggers it.
  if (session.session_id && (!urlOk || (Number.isFinite(expiresAt) && expiresAt - Date.now() < 60_000))) {
    let status: string | undefined;
    try {
      status = (await getCheckoutSession(session.session_id)).status;
    } catch {
      status = undefined;
    }
    if (status === 'paid') {
      window.location.href = `/checkout/success?session_id=${encodeURIComponent(session.session_id)}`;
      return;
    }
    if (status === 'expired' && !retriedExpired) {
      dropCurrentAttempt();
      return runCheckout(lines, true);
    }
    throw new CheckoutError('Your previous checkout is just closing. Please wait a minute and tap Checkout again.');
  }

  if (!urlOk) throw new CheckoutError(GENERIC);

  // Remember exactly what this session bought, for the confirmation page. If
  // this can't be saved, payment still works; that page just won't tidy the
  // bag or report the purchase to GA4.
  if (session.session_id) {
    noteAttemptSession(idempotencyKey, session.session_id);
    const analyticsItems: AnalyticsItem[] = lines.map((l) => ({
      item_id: l.productId,
      item_name: l.name,
      item_brand: l.brand,
      item_variant: l.variantTitle ?? undefined,
      price: l.priceCents / 100,
      quantity: l.qty,
    }));
    const pending: PendingCheckout = {
      sessionId: session.session_id,
      lines: lines.map((l) => ({ variantId: l.variantId, qty: l.qty })),
      items: analyticsItems,
      value: Math.round(lines.reduce((sum, l) => sum + l.priceCents * l.qty, 0)) / 100,
      createdAt: Date.now(),
    };
    writeJson('localStorage', pendingKey(pending.sessionId), pending);
  }

  window.location.href = url;
}
